import { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigation, useParams, useSearchParams } from 'react-router-dom';
import { algoliaClient } from '../../api/algolia/algolia.client';
import { ALG_API_KEY, ALG_APP_ID } from '../../constants/algolia';
import { MEDIA_BREAKPOINTS } from '../../constants/MEDIA_BREAKPOINTS';
import { listOfAllFacetKeys, nbItemsPerPage, searchableAttributes, sortConfigByNameOrIndex, useAnalytic, showFilterByTypes, activeFacetConfigs, fullListOfItemsForFacets, defaultSearchParams, queryLetter } from '../configs';
import { createFiltersString } from '../utils/createFiltersString';
import { getPartOfFilters } from '../utils/getPartOfFilters';
import { sortFacetValues } from '../utils/sortFacetValues';
import { FACET_KEY_COLORS, FACET_KEY_MEDIUMS, FACET_KEY_TAGS, FACET_KEY_TYPES } from '../constants/facetKeys';
import { SEARCH_PARAM_KEY_PAGE_NUMBER, SEARCH_PARAM_KEY_SORT, SEARCH_PARAM_KEY_TYPE } from '../constants/searchParamKeys';
import { convertSymbolsToEncodedUnicode } from '../../utils/convertSymbolsToEncodedUnicode';
import { useDynamicConfigs } from './useDynamicConfigs';
const appWrapper = document.querySelector('.alg-app');
const CLIENT = algoliaClient(ALG_APP_ID, ALG_API_KEY);
const queryParams = {
    analytics: useAnalytic,
    clickAnalytics: useAnalytic,
    hitsPerPage: nbItemsPerPage,
    page: 0,
    facets: listOfAllFacetKeys,
    ...(searchableAttributes !== undefined && { restrictSearchableAttributes: searchableAttributes }),
    filters: ''
};
let activeIndex;
let searchIndex;
const getInitialDataState = () => ({
    loading: true
});
const getInitialData = () => ({
    hits: [],
    facets: {},
    hierarchyFacets: {},
    facetsFromServer: {},
    nbHits: 0,
    nbPages: 0,
    queryID: ''
});
const transformFacetsData = (facetsFromServer, nbHits, sortingOrders) => {
    const facets = {};
    const hierarchyFacets = {};
    activeFacetConfigs.forEach(({ key, hierarchy, additionalKeys }) => {
        const valueIndexes = {};
        if (hierarchy !== undefined) {
            for (let lvlNum = 1; lvlNum <= hierarchy; lvlNum++) {
                const facetKey = `${key}_lv${lvlNum}`;
                const lvlFacetObj = facetsFromServer[facetKey];
                if (lvlFacetObj === undefined)
                    continue;
                if (lvlNum === 1) {
                    hierarchyFacets[key] = Object.entries(lvlFacetObj).map(([name, count], index) => {
                        valueIndexes[name] = index;
                        return {
                            label: name,
                            value: name.toLowerCase(),
                            facetKey,
                            count: Math.min(count, nbHits),
                            subs: []
                        };
                    });
                }
                else {
                    Object.entries(lvlFacetObj).forEach(([name, count]) => {
                        const names = name.split(' > ');
                        let subArray = hierarchyFacets[key];
                        let nameKey = '';
                        for (let nameIndex = 0; nameIndex < names.length; nameIndex++) {
                            const label = names[nameIndex];
                            nameKey = nameKey.length > 0
                                ? nameKey + ' > ' + label
                                : label;
                            if (nameIndex + 1 !== names.length) {
                                if (valueIndexes[nameKey] === undefined)
                                    break;
                                subArray = subArray[valueIndexes[nameKey]].subs;
                            }
                            else {
                                valueIndexes[nameKey] = subArray.length;
                                subArray.push({
                                    label,
                                    value: name.toLowerCase(),
                                    facetKey,
                                    count: Math.min(count, nbHits),
                                    subs: []
                                });
                            }
                        }
                    });
                }
            }
        }
        else if (facetsFromServer[key] !== undefined) {
            facets[key] = Object.entries(facetsFromServer[key]).map(([name, count], index) => {
                valueIndexes[name] = index;
                return {
                    label: name,
                    value: name.toLowerCase(),
                    count: Math.min(count, nbHits)
                };
            });
            if (additionalKeys !== undefined) {
                additionalKeys.forEach(addKey => {
                    if (facetsFromServer[addKey] === undefined)
                        return;
                    Object.entries(facetsFromServer[addKey]).forEach(([name, count]) => {
                        const valueIndex = valueIndexes[name];
                        if (valueIndex !== undefined) {
                            facets[key][valueIndex] = {
                                ...facets[key][valueIndex],
                                count: Math.min(facets[key][valueIndex].count + count, nbHits)
                            };
                        }
                        else {
                            const sepIndex = name.lastIndexOf(' > ');
                            facets[key].push({
                                label: sepIndex > -1 ? name.substring(sepIndex + 3) : name,
                                value: name.toLowerCase(),
                                count: Math.min(count, nbHits)
                            });
                        }
                    });
                });
            }
        }
    });
    if (showFilterByTypes && facetsFromServer[FACET_KEY_TYPES] !== undefined) {
        facets[FACET_KEY_TYPES] = Object.entries(facetsFromServer[FACET_KEY_TYPES]).map(([name, count]) => {
            return {
                label: name,
                value: name.toLowerCase(),
                count
            };
        });
    }
    if (facetsFromServer[FACET_KEY_TAGS] !== undefined) {
        facets[FACET_KEY_TAGS] = Object.entries(facetsFromServer[FACET_KEY_TAGS]).map(([name, count]) => {
            return {
                label: name,
                value: name.toLowerCase(),
                count
            };
        });
    }
    if (sortingOrders?.colors !== undefined && facets[FACET_KEY_COLORS] !== undefined) {
        facets[FACET_KEY_COLORS] = sortFacetValues(facets[FACET_KEY_COLORS], sortingOrders.colors.order);
    }
    if (sortingOrders?.types !== undefined && facets[FACET_KEY_TYPES] !== undefined) {
        facets[FACET_KEY_TYPES] = sortFacetValues(facets[FACET_KEY_TYPES], sortingOrders.types.order);
    }
    return { facets, hierarchyFacets };
};
const responseHandler = (response) => {
    const data = getInitialData();
    const facetsFromServer = response.facets;
    data.hits = response.hits;
    data.nbHits = response.nbSortedHits ?? response.nbHits;
    data.nbPages = response.nbPages;
    data.queryID = response.queryID;
    fullListOfItemsForFacets.forEach(([key, values]) => {
        facetsFromServer[key] = { ...values, ...facetsFromServer[key] };
    });
    data.facetsFromServer = facetsFromServer;
    const { facets, hierarchyFacets } = transformFacetsData(facetsFromServer, data.nbHits, response.renderingContent?.facetOrdering?.values);
    data.facets = facets;
    data.hierarchyFacets = hierarchyFacets;
    return data;
};
export const useAlgoliaSearch = () => {
    const [dataState, setDataState] = useState(getInitialDataState);
    const [data, setData] = useState(getInitialData);
    const [searchParams] = useSearchParams();
    const { defaultFilters = '', defaultQuery = '', collection } = useDynamicConfigs();
    const { medium, handle } = useParams();
    const { state } = useNavigation();
    useLayoutEffect(() => {
        setDataState({ loading: true });
    }, [searchParams, medium, handle, state]);
    useEffect(() => {
        let canceled = false;
        const filters = createFiltersString(convertSymbolsToEncodedUnicode(defaultFilters));
        const query = searchParams.get(queryLetter) ?? defaultQuery;
        queryParams.page = +(searchParams.get(SEARCH_PARAM_KEY_PAGE_NUMBER) ?? 1) - 1;
        queryParams.hitsPerPage =
            (collection?.capsuleItems !== undefined && collection.capsuleItems.length > 1) ||
                handle === 'photo-prints'
                ? nbItemsPerPage + 1
                : nbItemsPerPage;
        const sortConfig = sortConfigByNameOrIndex[searchParams.get(SEARCH_PARAM_KEY_SORT) ?? defaultSearchParams.index];
        const sortIndex = sortConfig?.index ?? defaultSearchParams.index;
        if (sortIndex !== activeIndex) {
            searchIndex = CLIENT.initIndex(sortIndex);
            activeIndex = sortIndex;
        }
        if (sortConfig?.filters !== undefined) {
            filters.add(sortConfig.filters);
        }
        if (showFilterByTypes) {
            const type = searchParams.get(SEARCH_PARAM_KEY_TYPE);
            if (type !== null) {
                filters.add(`${FACET_KEY_TYPES}:"${type}"`);
            }
        }
        const tagsValue = searchParams.get(FACET_KEY_TAGS);
        if (tagsValue !== null) {
            filters.add(getPartOfFilters(FACET_KEY_TAGS, tagsValue.split(',')));
        }
        if (medium !== undefined) {
            filters.add(`${FACET_KEY_MEDIUMS}:"${medium.replace(/-/g, ' ')}"`);
        }
        activeFacetConfigs.forEach(({ key, hierarchy, additionalKeys }) => {
            if (hierarchy === undefined) {
                const value = searchParams.get(key);
                if (value !== null) {
                    filters.add(getPartOfFilters(key, value.split(','), additionalKeys));
                }
            }
            else {
                for (let i = 1; i <= hierarchy; i++) {
                    const hKey = `${key}_lv${i}`;
                    const value = searchParams.get(hKey);
                    if (value !== null) {
                        filters.add(getPartOfFilters(hKey, value.split(','), additionalKeys?.map(key => `${key}_lv${i}`)));
                    }
                }
            }
        });
        queryParams.filters = filters.value();
        const wrapperRectTop = appWrapper.getBoundingClientRect().top;
        if (wrapperRectTop < 0) {
            window.scrollTo({
                top: wrapperRectTop + window.scrollY + (MEDIA_BREAKPOINTS.XL_UP.matches ? 250 : 195),
                behavior: 'smooth'
            });
        }
        void (async () => {
            const response = await searchIndex.search(convertSymbolsToEncodedUnicode(query), queryParams);
            if (!canceled) {
                setData(responseHandler(response));
                setDataState({ loading: false });
            }
        })();
        return () => { canceled = true; };
    }, [searchParams, medium, handle, state]);
    return [data, dataState];
};
